/**
 * System Status Page
 * Tabbed layout showing API health, database info, memory usage, logs, errors, and integrations
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTime } from '@/lib/utils';
import {
  Server,
  Database,
  Activity,
  Cpu,
  HardDrive,
  Wifi,
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  MessageSquare,
  Clock,
  Mail,
  Smartphone,
  LayoutDashboard,
  Cog,
  Bell,
} from 'lucide-react';
import { UsersTab } from './users-tab';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface StatusData {
  api: {
    status: string;
    uptime: number;
    startedAt: string;
    nodeVersion: string;
    environment: string;
    port: number | string;
  };
  system: {
    platform: string;
    arch: string;
    hostname: string;
    cpus: number;
    totalMemoryMb: number;
    freeMemoryMb: number;
    loadAvg: number[];
  };
  memory: {
    rss: number;
    heapTotal: number;
    heapUsed: number;
    external: number;
  };
  database: {
    status: string;
    version: string;
    databaseName: string;
    serverName: string;
    serverTime: string;
    totalSizeMb: string | null;
    usedSizeMb: string | null;
    schemas: Record<string, number>;
  };
  integrations: {
    sms: string;
    email: string;
  };
  requestStats: {
    totalRequests: number;
    successRate: number;
    avgDurationMs: number;
    errorCount: number;
    statusBreakdown: Record<string, number>;
    topPaths: { path: string; count: number }[];
  };
}

interface ApiLog {
  id: number;
  timestamp: string;
  method: string;
  path: string;
  statusCode: number;
  durationMs: number;
  ip: string;
  userAgent: string;
  userId?: string;
}

interface ErrorEntry {
  id: number;
  timestamp: string;
  method: string;
  path: string;
  statusCode: number;
  durationMs: number;
  ip: string;
  error?: string;
  errorDetail?: string;
}

interface ErrorSummaryData {
  path: string;
  method: string;
  statusCode: number;
  message: string;
  count: number;
  lastOccurred: string;
  firstOccurred: string;
}

interface NotificationLogEntry {
  id: number;
  timestamp: string;
  channel: string;
  type: string;
  recipient: string;
  subject: string;
  status: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'healthy' || status === 'connected' || status === 'configured') {
    return (
      <Badge className="bg-green-100 text-green-700 border-green-200">
        <CheckCircle className="h-3 w-3 mr-1" />{status}
      </Badge>
    );
  }
  if (status === 'not_configured') {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        <AlertTriangle className="h-3 w-3 mr-1" />not configured
      </Badge>
    );
  }
  return (
    <Badge variant="destructive">
      <XCircle className="h-3 w-3 mr-1" />{status}
    </Badge>
  );
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-700',
    POST: 'bg-green-100 text-green-700',
    PATCH: 'bg-yellow-100 text-yellow-700',
    PUT: 'bg-orange-100 text-orange-700',
    DELETE: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${colors[method] || 'bg-gray-100 text-gray-700'}`}>
      {method}
    </span>
  );
}

function StatusCodeBadge({ code }: { code: number }) {
  const cls = code < 300
    ? 'text-green-700'
    : code < 400
    ? 'text-blue-700'
    : code < 500
    ? 'text-orange-700'
    : 'text-red-700';
  return <span className={`text-xs font-mono font-bold ${cls}`}>{code}</span>;
}

// ---------------------------------------------------------------------------
// Tab: Services
// ---------------------------------------------------------------------------

function ServicesTab({ status }: { status: StatusData | undefined }) {
  const memPercent = status
    ? Math.round((status.memory.heapUsed / status.memory.heapTotal) * 100)
    : 0;

  const sysmemPercent = status
    ? Math.round(((status.system.totalMemoryMb - status.system.freeMemoryMb) / status.system.totalMemoryMb) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* System Resources + Database Info */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="h-4 w-4 text-muted-foreground" />
              System Resources
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">System Memory</span>
                <span className="font-medium">{sysmemPercent}% used ({(status?.system.totalMemoryMb ?? 0) - (status?.system.freeMemoryMb ?? 0)} / {status?.system.totalMemoryMb ?? 0} MB)</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${sysmemPercent > 85 ? 'bg-red-500' : sysmemPercent > 70 ? 'bg-orange-500' : 'bg-green-500'}`} style={{ width: `${sysmemPercent}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-muted-foreground">Heap Memory (Node)</span>
                <span className="font-medium">{memPercent}% ({status?.memory.heapUsed} / {status?.memory.heapTotal} MB)</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${memPercent > 85 ? 'bg-red-500' : memPercent > 70 ? 'bg-orange-500' : 'bg-primary'}`} style={{ width: `${memPercent}%` }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2 border-t text-sm">
              <div>
                <p className="text-muted-foreground">Platform</p>
                <p className="font-medium">{status?.system.platform} ({status?.system.arch})</p>
              </div>
              <div>
                <p className="text-muted-foreground">CPUs</p>
                <p className="font-medium">{status?.system.cpus} cores</p>
              </div>
              <div>
                <p className="text-muted-foreground">Load Avg (1m)</p>
                <p className="font-medium">{status?.system.loadAvg?.[0]?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Environment</p>
                <p className="font-medium capitalize">{status?.api.environment}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              Database Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-muted-foreground">Server</p>
                  <p className="font-medium truncate">{status?.database.serverName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Database</p>
                  <p className="font-medium">{status?.database.databaseName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Server Time (UTC)</p>
                  <p className="font-medium text-xs">{status?.database.serverTime ? formatDateTime(status.database.serverTime) : '\u2014'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Version</p>
                  <p className="font-medium text-xs truncate">{status?.database.version?.split('\t')[0] || '\u2014'}</p>
                </div>
              </div>
              {status?.database.usedSizeMb && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground text-xs">Size: {status.database.usedSizeMb} MB used / {status.database.totalSizeMb} MB total</p>
                </div>
              )}
              {status?.database.schemas && Object.keys(status.database.schemas).length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-muted-foreground mb-2">Schema Tables</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(status.database.schemas).map(([schema, count]) => (
                      <span key={schema} className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs">
                        <span className="font-medium">{schema}</span>
                        <span className="text-muted-foreground">{count} tables</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Integrations + Background Services */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Wifi className="h-4 w-4 text-muted-foreground" />
              Integrations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">SMS (Twilio)</span>
                </div>
                <StatusBadge status={status?.integrations.sms || 'unknown'} />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Email (SendGrid)</span>
                </div>
                <StatusBadge status={status?.integrations.email || 'unknown'} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Cog className="h-4 w-4 text-muted-foreground" />
              Background Services
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: 'SLA Checker', description: 'Monitors ticket SLA compliance' },
                { name: 'Auto-close Checker', description: 'Closes stale resolved tickets' },
                { name: 'Report Scheduler', description: 'Generates scheduled reports' },
              ].map((svc) => (
                <div key={svc.name} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{svc.name}</p>
                    <p className="text-xs text-muted-foreground">{svc.description}</p>
                  </div>
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" />Active
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Database
// ---------------------------------------------------------------------------

function DatabaseTab({ activeTab }: { activeTab: string }) {
  const { data: dbTablesData } = useQuery({
    queryKey: ['db-tables'],
    queryFn: () => api.getDbTables(),
    enabled: activeTab === 'database',
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const dbTables = (dbTablesData?.data || []) as { schema: string; table: string; rowCount: number; totalSpaceMb: number }[];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Database className="h-4 w-4 text-muted-foreground" />
          Database Tables
        </CardTitle>
      </CardHeader>
      <CardContent>
        {dbTables.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">Loading tables...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-xs">
                  <th className="text-left py-2 pr-4">Schema</th>
                  <th className="text-left py-2 pr-4">Table</th>
                  <th className="text-right py-2 pr-4">Rows</th>
                  <th className="text-right py-2">Size (MB)</th>
                </tr>
              </thead>
              <tbody>
                {dbTables.map((t, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 pr-4">
                      <span className="inline-flex px-1.5 py-0.5 bg-muted rounded text-xs font-mono">{t.schema}</span>
                    </td>
                    <td className="py-2 pr-4 font-medium">{t.table}</td>
                    <td className="py-2 pr-4 text-right font-mono">{t.rowCount.toLocaleString()}</td>
                    <td className="py-2 text-right text-muted-foreground">{t.totalSpaceMb?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab: API Logs
// ---------------------------------------------------------------------------

function ApiLogsTab({ activeTab, status }: { activeTab: string; status: StatusData | undefined }) {
  const [logSearch, setLogSearch] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('');

  const { data: logsData, isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['api-logs', logStatusFilter],
    queryFn: () => api.getApiLogs({ limit: 200, status: logStatusFilter || undefined }),
    enabled: activeTab === 'api-logs',
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const rawLogs = (logsData?.data || []) as ApiLog[];
  const filteredLogs = logSearch
    ? rawLogs.filter((l) => l.path.includes(logSearch) || l.ip.includes(logSearch))
    : rawLogs;

  return (
    <div className="space-y-4">
      {/* API Request Logs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              API Request Logs
              {logsLoading && <span className="text-xs text-muted-foreground font-normal ml-2">Loading...</span>}
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Filter by path or IP..."
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  className="pl-8 h-8 text-sm w-52"
                />
              </div>
              {['', '2xx', '4xx', '5xx'].map((f) => (
                <Button
                  key={f || 'all'}
                  variant={logStatusFilter === f ? 'secondary' : 'outline'}
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setLogStatusFilter(f)}
                >
                  {f || 'All'}
                </Button>
              ))}
              <Button variant="ghost" size="sm" className="h-8" onClick={() => refetchLogs()}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-2 pr-3">Time</th>
                  <th className="text-left py-2 pr-3">Method</th>
                  <th className="text-left py-2 pr-3">Path</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-right py-2 pr-3 flex items-center gap-1 justify-end">
                    <Clock className="h-3 w-3" /> Duration
                  </th>
                  <th className="text-left py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.slice(0, 100).map((log) => (
                  <tr key={log.id} className={`border-b last:border-0 hover:bg-muted/30 ${log.statusCode >= 500 ? 'bg-red-50' : log.statusCode >= 400 ? 'bg-orange-50' : ''}`}>
                    <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </td>
                    <td className="py-1.5 pr-3">
                      <MethodBadge method={log.method} />
                    </td>
                    <td className="py-1.5 pr-3 font-mono max-w-xs truncate" title={log.path}>
                      {log.path}
                    </td>
                    <td className="py-1.5 pr-3">
                      <StatusCodeBadge code={log.statusCode} />
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                      {log.durationMs}ms
                    </td>
                    <td className="py-1.5 text-muted-foreground">{log.ip}</td>
                  </tr>
                ))}
                {filteredLogs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      No logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* HTTP Status Breakdown + Top Endpoints */}
      {status?.requestStats.statusBreakdown && Object.keys(status.requestStats.statusBreakdown).length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">HTTP Status Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(status.requestStats.statusBreakdown).map(([bucket, count]) => {
                  const total = Object.values(status.requestStats.statusBreakdown).reduce((a, b) => a + b, 0);
                  const pct = Math.round((count / total) * 100);
                  const color = bucket.startsWith('2') ? 'bg-green-500' : bucket.startsWith('4') ? 'bg-orange-500' : bucket.startsWith('5') ? 'bg-red-500' : 'bg-blue-500';
                  return (
                    <div key={bucket} className="flex items-center gap-3">
                      <span className="text-xs font-mono w-8">{bucket}</span>
                      <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                        <div className={`h-full ${color} rounded`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-16 text-right">{count} ({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Top Endpoints</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {status.requestStats.topPaths.slice(0, 8).map(({ path, count }) => {
                  const maxCount = status.requestStats.topPaths[0]?.count || 1;
                  return (
                    <div key={path} className="flex items-center gap-2">
                      <span className="text-xs font-mono truncate flex-1" title={path}>{path}</span>
                      <div className="w-20 h-3 bg-muted rounded overflow-hidden">
                        <div className="h-full bg-primary/70 rounded" style={{ width: `${(count / maxCount) * 100}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: API Errors
// ---------------------------------------------------------------------------

function ApiErrorsTab({ activeTab }: { activeTab: string }) {
  const [errorPathFilter, setErrorPathFilter] = useState('');

  const { data: errorsData, refetch: refetchErrors } = useQuery({
    queryKey: ['api-errors'],
    queryFn: () => api.getApiErrors({ limit: 100 }),
    enabled: activeTab === 'api-errors',
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const errorResult = errorsData?.data as { errors?: ErrorEntry[]; summaries?: ErrorSummaryData[]; stats?: { recentErrorCount?: number; topErrors?: { path: string; method: string; statusCode: number; count: number }[]; slowEndpoints?: { path: string; method: string; avgDurationMs: number; count: number }[] } } | undefined;
  const errors = (errorResult?.errors || []);
  const summaries = (errorResult?.summaries || []);
  const stats = errorResult?.stats;
  const slowEndpoints = stats?.slowEndpoints || [];

  const filteredErrors = errorPathFilter
    ? errors.filter((e) => e.path.includes(errorPathFilter) || (e.errorDetail || '').includes(errorPathFilter))
    : errors;

  return (
    <div className="space-y-4">
      {/* Error summary header */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Recent Errors</p>
                <p className={`text-2xl font-bold ${errors.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {errors.length}
                </p>
              </div>
              <XCircle className={`h-8 w-8 ${errors.length > 0 ? 'text-red-200' : 'text-green-200'}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unique Error Patterns</p>
                <p className="text-2xl font-bold">{summaries.length}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Slow Endpoints</p>
                <p className="text-2xl font-bold">{slowEndpoints.length}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Top Error Patterns */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              Top Error Patterns
            </CardTitle>
          </CardHeader>
          <CardContent>
            {summaries.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No errors recorded</p>
            ) : (
              <div className="space-y-3">
                {summaries.slice(0, 8).map((s, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <Badge variant={s.statusCode >= 500 ? 'destructive' : 'warning'} className="shrink-0 mt-0.5">
                      {s.statusCode}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <MethodBadge method={s.method} />
                        <span className="font-mono text-xs truncate">{s.path}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate" title={s.message}>{s.message}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <span className="text-xs font-bold text-red-600">{s.count}x</span>
                      <p className="text-[10px] text-muted-foreground">{formatDateTime(s.lastOccurred)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Slow Endpoints */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              Slow Endpoints (avg &gt; 100ms)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {slowEndpoints.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">All endpoints performing well</p>
            ) : (
              <div className="space-y-2">
                {slowEndpoints.map((ep, i) => {
                  const maxDuration = slowEndpoints[0]?.avgDurationMs || 1;
                  const barColor = ep.avgDurationMs > 500 ? 'bg-red-400' : ep.avgDurationMs > 200 ? 'bg-orange-400' : 'bg-yellow-400';
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <MethodBadge method={ep.method} />
                      <span className="text-xs font-mono truncate flex-1" title={ep.path}>{ep.path}</span>
                      <div className="w-20 h-3 bg-muted rounded overflow-hidden">
                        <div className={`h-full ${barColor} rounded`} style={{ width: `${(ep.avgDurationMs / maxDuration) * 100}%` }} />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground w-16 text-right">{ep.avgDurationMs}ms</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Error Log */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <XCircle className="h-4 w-4 text-red-500" />
              Recent Error Log
            </CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Filter errors..."
                  value={errorPathFilter}
                  onChange={(e) => setErrorPathFilter(e.target.value)}
                  className="pl-8 h-8 text-sm w-52"
                />
              </div>
              <Button variant="ghost" size="sm" className="h-8" onClick={() => refetchErrors()}>
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-2 pr-3">Time</th>
                  <th className="text-left py-2 pr-3">Method</th>
                  <th className="text-left py-2 pr-3">Path</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-left py-2 pr-3">Error</th>
                  <th className="text-right py-2 pr-3">Duration</th>
                  <th className="text-left py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {filteredErrors.slice(0, 50).map((err) => (
                  <tr key={err.id} className={`border-b last:border-0 hover:bg-muted/30 ${err.statusCode >= 500 ? 'bg-red-50' : 'bg-orange-50'}`}>
                    <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">
                      {new Date(err.timestamp).toLocaleTimeString()}
                    </td>
                    <td className="py-1.5 pr-3"><MethodBadge method={err.method} /></td>
                    <td className="py-1.5 pr-3 font-mono max-w-[200px] truncate" title={err.path}>{err.path}</td>
                    <td className="py-1.5 pr-3"><StatusCodeBadge code={err.statusCode} /></td>
                    <td className="py-1.5 pr-3 max-w-[300px] truncate text-red-600" title={err.errorDetail || err.error}>
                      {err.errorDetail || err.error || '-'}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">{err.durationMs}ms</td>
                    <td className="py-1.5 text-muted-foreground">{err.ip}</td>
                  </tr>
                ))}
                {filteredErrors.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-muted-foreground">
                      No errors recorded
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Email / SMS
// ---------------------------------------------------------------------------

function EmailSmsTab({ activeTab, status }: { activeTab: string; status: StatusData | undefined }) {
  const { data: notifLogData } = useQuery({
    queryKey: ['notification-log'],
    queryFn: () => api.getNotificationLog({ limit: 50 }),
    enabled: activeTab === 'email-sms',
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const notifications = (notifLogData?.data || []) as NotificationLogEntry[];

  return (
    <div className="space-y-4">
      {/* Integration status cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="h-5 w-5 text-blue-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Email (SendGrid)</p>
                <StatusBadge status={status?.integrations.email || 'unknown'} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Smartphone className="h-5 w-5 text-green-700" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">SMS (Twilio)</p>
                <StatusBadge status={status?.integrations.sms || 'unknown'} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notes */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">Email logs can be viewed in <span className="font-medium text-foreground">Billing &gt; Email Logs</span></p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <Smartphone className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-sm text-muted-foreground">SMS notifications are sent to admin phone on new helpdesk tickets</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notification Log */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            Notification Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-2 pr-3">Timestamp</th>
                  <th className="text-left py-2 pr-3">Channel</th>
                  <th className="text-left py-2 pr-3">Type</th>
                  <th className="text-left py-2 pr-3">Recipient</th>
                  <th className="text-left py-2 pr-3">Subject</th>
                  <th className="text-left py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((n, i) => {
                  const channelColor =
                    n.channel === 'email' ? 'bg-blue-100 text-blue-700' :
                    n.channel === 'sms' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700';
                  return (
                    <tr key={n.id ?? i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="py-1.5 pr-3 text-muted-foreground whitespace-nowrap">
                        {formatDateTime(n.timestamp)}
                      </td>
                      <td className="py-1.5 pr-3">
                        <Badge variant="outline" className={channelColor}>
                          {n.channel === 'in_app' ? 'in-app' : n.channel}
                        </Badge>
                      </td>
                      <td className="py-1.5 pr-3 font-mono">{n.type}</td>
                      <td className="py-1.5 pr-3 max-w-[200px] truncate" title={n.recipient}>{n.recipient}</td>
                      <td className="py-1.5 pr-3 max-w-[250px] truncate" title={n.subject}>{n.subject}</td>
                      <td className="py-1.5">
                        {n.status === 'sent' ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">sent</Badge>
                        ) : (
                          <Badge variant="destructive">failed</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {notifications.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      No notification logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Widgets
// ---------------------------------------------------------------------------

function WidgetsTab({ status }: { status: StatusData | undefined }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <LayoutDashboard className="h-5 w-5 text-purple-700" />
            </div>
            <div>
              <p className="text-sm font-medium">Widget Management</p>
              <p className="text-sm text-muted-foreground mt-1">
                Widget management is available at <span className="font-medium text-foreground">Analytics &gt; Widget Management</span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {status && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
              Widget Key Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">API Status</p>
                <p className="font-medium capitalize">{status.api.status}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Environment</p>
                <p className="font-medium capitalize">{status.api.environment}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function SystemStatusPage() {
  const [activeTab, setActiveTab] = useState('services');

  const { data: statusData, isLoading: statusLoading, refetch: refetchStatus } = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api.getSystemStatus(),
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: false,
  });

  const status = statusData?.data as StatusData | undefined;

  const handleRefresh = () => {
    refetchStatus();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">System Status</h1>
          <p className="text-muted-foreground">Real-time API health, database info, and request logs</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {statusLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {/* Top Status Strip - always visible */}
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            {/* API Server */}
            <Card className="border-l-4 border-l-primary">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Server className="h-4 w-4 text-primary shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-muted-foreground">API Server</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusBadge status={status?.api.status || 'unknown'} />
                      <span className="text-[10px] text-muted-foreground">{formatUptime(status?.api.uptime || 0)}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Database */}
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Database className="h-4 w-4 text-blue-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-muted-foreground">Database</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusBadge status={status?.database.status || 'unknown'} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Success Rate */}
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Activity className="h-4 w-4 text-purple-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-muted-foreground">Success Rate</p>
                    <p className="text-lg font-bold leading-tight">{status?.requestStats.successRate ?? 100}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Integrations */}
            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Wifi className="h-4 w-4 text-orange-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-muted-foreground">Integrations</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-muted-foreground">SMS</span>
                      <StatusBadge status={status?.integrations.sms || 'unknown'} />
                      <span className="text-[10px] text-muted-foreground ml-1">Email</span>
                      <StatusBadge status={status?.integrations.email || 'unknown'} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabbed Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="database">Database</TabsTrigger>
              <TabsTrigger value="api-logs">API Logs</TabsTrigger>
              <TabsTrigger value="api-errors">API Errors</TabsTrigger>
              <TabsTrigger value="email-sms">Email / SMS</TabsTrigger>
              <TabsTrigger value="widgets">Widgets</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
            </TabsList>

            <TabsContent value="services" className="mt-4">
              <ServicesTab status={status} />
            </TabsContent>

            <TabsContent value="database" className="mt-4">
              <DatabaseTab activeTab={activeTab} />
            </TabsContent>

            <TabsContent value="api-logs" className="mt-4">
              <ApiLogsTab activeTab={activeTab} status={status} />
            </TabsContent>

            <TabsContent value="api-errors" className="mt-4">
              <ApiErrorsTab activeTab={activeTab} />
            </TabsContent>

            <TabsContent value="email-sms" className="mt-4">
              <EmailSmsTab activeTab={activeTab} status={status} />
            </TabsContent>

            <TabsContent value="widgets" className="mt-4">
              <WidgetsTab status={status} />
            </TabsContent>

            <TabsContent value="users" className="mt-4">
              <UsersTab />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
